import React, { useState } from 'react';
import axios from 'axios';
import { Users, Mail, Phone, User, MessageSquare, Quote } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Reserve = () => {
  const [heroRef, heroVisible] = useScrollAnimation(0.1);
  const [formRef, formVisible] = useScrollAnimation(0.2);
  const [eventbriteRef] = useScrollAnimation(0.2);
  const [testimonialsRef, testimonialsVisible] = useScrollAnimation(0.2);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    party_size: 2,
    special_requests: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Store inquiry in database
      await axios.post(`${API}/reservations`, {
        ...formData,
        date: 'Contact for available dates',
        time: 'TBD'
      });
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        party_size: 2,
        special_requests: ''
      });
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a minute and try again.');
      } else {
        setError(typeof detail === 'string' ? detail : 'Please check your details and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative h-96 flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/gu4hfhmj_IMG_0103.jpeg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 hero-bg-zoom" style={{backgroundImage: "url('https://customer-assets.emergentagent.com/job_client-web-setup/artifacts/gu4hfhmj_IMG_0103.jpeg')", backgroundSize: 'cover', backgroundPosition: 'center'}}></div>
        <div className="absolute inset-0 image-overlay-luxury"></div>
        <div className={`relative z-10 text-center px-4 ${heroVisible ? 'animate-fade-in-up' : 'opacity-0'}`} data-testid="reserve-hero">
          <h1 className="text-5xl md:text-7xl font-bold text-shadow-luxury mb-6" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Ready to Experience a Proper Meal?</h1>
          <p className="text-2xl mt-6 italic max-w-3xl mx-auto text-shadow-luxury">
            Bring your family or friends, or just yourself, and enjoy a warm, welcoming atmosphere with great food and even better company.
          </p>
        </div>
      </section>

      {/* Reservation Form */}
      <section ref={formRef} className="py-20 px-4 bg-cream-dark">
        <div className={`max-w-3xl mx-auto ${formVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="bg-white p-8 md:p-12 rounded-lg shadow-xl">
            {success && (
              <div className="mb-8 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" data-testid="success-message">
                <h3 className="font-bold text-lg mb-2">Enquiry Received! 🎉</h3>
                <p>Thank you for your enquiry — we've got your details and will be in touch shortly to arrange your visit to Mama-Mam's Kitchen!</p>
              </div>
            )}

            {error && (
              <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
                {error}
              </div>
            )}

            <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Book Your Sunday Table</h2>

            <form onSubmit={handleSubmit} data-testid="reservation-form">
              {/* Name */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  <User className="inline w-5 h-5 mr-2" />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  placeholder="John Doe"
                  data-testid="input-name"
                />
              </div>

              {/* Email */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  <Mail className="inline w-5 h-5 mr-2" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  placeholder="john@example.com"
                  data-testid="input-email"
                />
              </div>

              {/* Phone */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  <Phone className="inline w-5 h-5 mr-2" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  placeholder="07XXX XXXXXX"
                  data-testid="input-phone"
                />
              </div>

              {/* Party Size */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  <Users className="inline w-5 h-5 mr-2" />
                  Party Size *
                </label>
                <select
                  name="party_size"
                  value={formData.party_size}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  data-testid="input-party-size"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>{num} {num === 1 ? 'Guest' : 'Guests'}</option>
                  ))}
                </select>
              </div>

              {/* Special Requests */}
              <div className="mb-8">
                <label className="block text-gray-700 font-semibold mb-2">
                  <MessageSquare className="inline w-5 h-5 mr-2" />
                  Message / Special Requests (Optional)
                </label>
                <textarea
                  name="special_requests"
                  value={formData.special_requests}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                  placeholder="Any dietary restrictions, preferred dates, or special occasions?"
                  data-testid="input-special-requests"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gold hover:bg-brown text-white font-bold py-5 px-8 rounded-lg text-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-2xl transform hover:scale-105"
                data-testid="submit-reservation-button"
              >
                {loading ? 'Sending...' : 'Reserve Your Spot Now →'}
              </button>
            </form>
          </div>

          {/* Info Box */}
          <div ref={eventbriteRef} className={`mt-8 bg-white p-6 rounded-lg shadow-md ${formVisible ? 'animate-fade-in-up delay-200' : 'opacity-0'}`}>
            <h3 className="text-xl font-bold text-gray-800 mb-4">What to Expect</h3>
            <ul className="space-y-2 text-gray-700">
              <li>All-you-can-eat buffet experience</li>
              <li>Fresh, home-cooked Afro Caribbean comfort food</li>
              <li>Drinks</li>
              <li>Family-friendly atmosphere</li>
              <li>Live music and entertainment</li>
              <li>Pricing varies by event - contact us for details</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <NewsletterSection />

      {/* Testimonial After Booking */}
      <section ref={testimonialsRef} className="py-16 px-4 bg-white">
        <div className={`max-w-3xl mx-auto ${testimonialsVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
          <div className="bg-cream-dark p-12 rounded-lg shadow-luxury-hover border-l-4 border-gold">
            <Quote className="w-12 h-12 text-gold mb-4 mx-auto" />
            <p className="text-2xl text-gray-700 italic leading-relaxed mb-6 text-center">
              "Lovely event. We thoroughly enjoyed the day/evening - so glad we could make it. Well done lil girlfriend"
            </p>
            <p className="font-bold text-brown text-xl text-center">— Dot Wak-Williams</p>
          </div>
        </div>
      </section>
    </div>
  );
};

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      await axios.post(`${API}/newsletter`, { email, name });
      setSuccess(true);
      setEmail('');
      setName('');
    } catch (err) {
      setError('Failed to subscribe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 px-4 bg-gold text-white">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: "'Cormorant Garamond', Georgia, serif"}}>Join Our Sunday Supper Club</h2>
        <p className="text-xl mb-8">Get menu updates, special offers, and first dibs on event announcements!</p>

        {success && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg" data-testid="newsletter-success">
            <p className="font-semibold">Welcome to the family! 🎉</p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="max-w-xl mx-auto" data-testid="newsletter-form">
          <div className="flex flex-col md:flex-row gap-4">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your Name"
              className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-white"
              data-testid="newsletter-name"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Your Email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-800 focus:ring-2 focus:ring-white"
              data-testid="newsletter-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="bg-white text-brown hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg transition-all disabled:opacity-50"
              data-testid="newsletter-submit-button"
            >
              {loading ? 'Joining...' : 'Join Now'}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default Reserve;
